import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EventEmitter from 'react-native-eventemitter';
import { useMutation } from 'react-query';
import moment from 'moment';
import { trackingEvent } from '../../../analytics';
import { getBasicAmplitudEventProperties } from '../../../analytics/utils';
import { AMPLITUDE_SERVICE } from '../../../analytics/constants';
import { treePaneService } from '../../../services/activityTreeSettings.service';
import notificationSystemV2 from '../../DesignSystem/NotificationSystemV2/NotificationSystemV2';
import * as Sentry from '@sentry/react';

export const useDateRangeModal = (setIsDateRangeModalVisible) => {
  const { t } = useTranslation();

  const defaultView = window.defaultView?.current;

  const { mutateAsync } = useMutation({
    mutationFn: (data) =>
      treePaneService.selectedActivityTreeByDateRange({
        ...data,
        range: 'date',
        viewId: defaultView?.id,
        scheduleId: defaultView?.sectorId
      }),
    retry: 0
  });

  const [datesSelected, setDatesSelected] = useState([]);

  useEffect(() => {
    if (!defaultView) {
      Sentry.withScope((scope) => {
        scope.setContext('useDateRangeModal', {
          message: 'defaultView is not defined'
        });
        Sentry.captureException('defaultView is not defined');
      });
    }
  }, [defaultView]);

  const handleDateSelection = (dates) => {
    setDatesSelected(dates);
  };

  const handleCloseDateRangeModal = () => {
    setIsDateRangeModalVisible(false);
    setDatesSelected([]);
  };

  const handleApplyDateRange = async () => {
    try {
      if (datesSelected.length === 0) {
        return;
      }

      const response = await mutateAsync({
        startDate: moment(datesSelected[0])
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment(datesSelected[1])
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ss')
      });

      if (response.isError) {
        return;
      }

      EventEmitter.emit('HIDE_PLANIFICATION');

      trackingEvent(
        'timeframe_filter_toolbar',
        {
          ...getBasicAmplitudEventProperties(),
          filter_option: 'date'
        },
        AMPLITUDE_SERVICE
      );

      if (response.data.isDefaultActivities) {
        notificationSystemV2({
          key: 'time-frame-empty-activities',
          type: 'warning',
          message: t(
            'lookahead_visualizationOpt.time_frame_empty_activities_message'
          )
        });
      }

      if (!response.data.isPartialSavingActivities) {
        return;
      }

      notificationSystemV2({
        key: 'partially-saved-activities',
        type: 'warning',
        message: t('lookahead_visualizationOpt.time_frame_exceed_limit_message')
      });

      trackingEvent(
        'timeframe_filter_data_limit_reached',
        {
          ...getBasicAmplitudEventProperties(),
          viewId: defaultView?.id,
          source: 'timeframe',
          timeframe_selected: 'date',
          total_items_requested: response.data.totalActivities,
          max_items_loaded: response.data.maxItems
        },
        AMPLITUDE_SERVICE
      );
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setContext('handle Select Item', {
          message: err.message
        });
        Sentry.captureException(err);
      });
    }
  };

  return {
    datesSelected,
    handleDateSelection,
    handleCloseDateRangeModal,
    handleApplyDateRange
  };
};
